import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  addCorrectiveAction,
  closeFinding,
  createAuditEngagement,
  createAuditProgramme,
  createAuditorShare,
  createFinding,
  getMe,
  listAuditProgrammes,
  listAuditorShares,
  listComplianceControls,
  listFindings,
  listUsers,
  revokeAuditorShare,
  setEngagementScope,
  updateAuditEngagement,
  updateCorrectiveAction,
  verifyCorrectiveAction,
  type FindingStage,
  type FindingType,
  type FindingView,
} from "@/lib/api";
import { Modal } from "@/components/Modal";

const TYPE_STYLE: Record<FindingType, { label: string; cls: string }> = {
  major_nc: { label: "Major NC", cls: "bg-danger/15 text-danger" },
  minor_nc: { label: "Minor NC", cls: "bg-warning/15 text-warning" },
  observation: { label: "Observation", cls: "bg-accent-muted text-accent" },
  ofi: { label: "OFI", cls: "border border-border text-text-secondary" },
};

const STAGE_LABEL: Record<FindingStage, string> = {
  open: "Open",
  root_cause: "Root cause",
  action_planned: "Action planned",
  actioned: "Actioned",
  verified: "Verified",
  closed: "Closed",
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

type Tab = "findings" | "programme" | "share";

export function ComplianceAuditPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("findings");
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });

  return (
    <div>
      <header className="mb-6">
        <h1 className="page-title">Internal audit</h1>
        <p className="mt-2 text-text-secondary">
          The audit programme, what it found, and what was done about it — including the
          check that the fix actually worked, which is the step 10.1 asks for and almost
          everyone skips.
        </p>
      </header>

      <div className="mb-6 flex gap-1 border-b border-border">
        {(
          [
            { key: "findings", label: "Findings", icon: "fa-flag" },
            { key: "programme", label: "Programme", icon: "fa-calendar-days" },
            { key: "share", label: "Auditor access", icon: "fa-link" },
          ] as { key: Tab; label: string; icon: string }[]
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
              tab === t.key
                ? "border-accent text-text-heading"
                : "border-transparent text-text-secondary hover:text-text-heading"
            }`}
          >
            <i className={`fa-solid ${t.icon} mr-1.5 text-xs`} />
            {t.label}
          </button>
        ))}
      </div>

      {tab === "findings" && <FindingsTab params={params} setParams={setParams} />}
      {tab === "programme" && <ProgrammeTab />}
      {tab === "share" && <ShareTab isAdmin={me?.role === "admin"} />}
    </div>
  );
}

/* ── Findings ──────────────────────────────────────────────────────────── */

function FindingsTab({
  params,
  setParams,
}: {
  params: URLSearchParams;
  setParams: (p: Record<string, string>) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const { data: findings, isLoading } = useQuery({
    queryKey: ["compliance-findings"],
    queryFn: listFindings,
  });

  const openId = params.get("open");
  const open = useMemo(
    () => findings?.find((f) => f.finding.id === openId) ?? null,
    [findings, openId],
  );
  const shown = (findings ?? []).filter((f) => showClosed || f.stage !== "closed");

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>
        <button
          onClick={() => setAddOpen(true)}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
        >
          Raise finding
        </button>
      </div>

      {isLoading ? (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
          <i className="fa-solid fa-circle-notch fa-spin text-lg" />
        </div>
      ) : shown.length === 0 ? (
        <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
          No open findings. Findings come from internal audits, incidents, management
          review, or simply noticing something — all of them belong here.
        </p>
      ) : (
        <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
                <th className="px-4 py-2.5 font-semibold">Ref</th>
                <th className="px-4 py-2.5 font-semibold">Finding</th>
                <th className="px-4 py-2.5 font-semibold">Type</th>
                <th className="px-4 py-2.5 font-semibold">Control</th>
                <th className="px-4 py-2.5 font-semibold">Raised</th>
                <th className="px-4 py-2.5 font-semibold">Stage</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((v) => (
                <tr
                  key={v.finding.id}
                  className="cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                  onClick={() => setParams({ open: v.finding.id })}
                >
                  <td className="whitespace-nowrap px-4 py-2.5 font-medium text-text-heading">
                    {v.finding.ref}
                  </td>
                  <td className="px-4 py-2.5 text-text-heading">{v.finding.title}</td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_STYLE[v.finding.type].cls}`}
                    >
                      {TYPE_STYLE[v.finding.type].label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">
                    {v.finding.control?.ref ?? "—"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {v.finding.raisedOn}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {STAGE_LABEL[v.stage]}
                    {v.blockers.length > 0 && (
                      <span className="ml-2 text-xs text-warning">{v.blockers.length}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && <FindingModal view={open} onClose={() => setParams({})} />}
      {addOpen && <RaiseFindingModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}

function RaiseFindingModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<FindingType>("minor_nc");
  const { data: controls } = useQuery({
    queryKey: ["compliance-controls"],
    queryFn: listComplianceControls,
  });
  const [controlId, setControlId] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createFinding({
        title: title.trim(),
        description: description.trim(),
        type,
        controlId: controlId || null,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["compliance-findings"] });
      void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="Raise a finding"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!title.trim() || !description.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Raise
        </button>
      }
    >
      <div className="space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Two leavers retained GitHub access"
          className={inputCls}
          autoFocus
        />
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="What was seen, and against which requirement — the evidence for the finding."
          className={inputCls}
        />
        <label className="block text-xs text-text-muted">
          Type
          <select
            value={type}
            onChange={(e) => setType(e.target.value as FindingType)}
            className={inputCls}
          >
            <option value="major_nc">Major nonconformity — systemic or total failure</option>
            <option value="minor_nc">Minor nonconformity — isolated lapse</option>
            <option value="observation">Observation — not a nonconformity</option>
            <option value="ofi">Opportunity for improvement</option>
          </select>
        </label>
        <label className="block text-xs text-text-muted">
          Control (optional)
          <select
            value={controlId}
            onChange={(e) => setControlId(e.target.value)}
            className={inputCls}
          >
            <option value="">— none —</option>
            {controls?.map((c) => (
              <option key={c.control.id} value={c.control.id}>
                {c.control.ref} · {c.control.name}
              </option>
            ))}
          </select>
        </label>
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

function FindingModal({ view, onClose }: { view: FindingView; onClose: () => void }) {
  const qc = useQueryClient();
  const f = view.finding;
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: getMe });
  const [action, setAction] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [targetDate, setTargetDate] = useState(plusDays(30));
  const [verifyFor, setVerifyFor] = useState<string | null>(null);
  const [verifyNote, setVerifyNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["compliance-findings"] });
    void qc.invalidateQueries({ queryKey: ["compliance-overview"] });
  };
  const fail = (e: Error) => setError(e.message);

  const add = useMutation({
    mutationFn: () =>
      addCorrectiveAction(f.id, {
        action,
        rootCause: rootCause || null,
        ownerId: me?.id ?? null,
        targetDate,
      }),
    onSuccess: () => {
      setAction("");
      setRootCause("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const patch = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Parameters<typeof updateCorrectiveAction>[1] }) =>
      updateCorrectiveAction(id, input),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const verify = useMutation({
    mutationFn: (id: string) => verifyCorrectiveAction(id, verifyNote),
    onSuccess: () => {
      setVerifyFor(null);
      setVerifyNote("");
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const close = useMutation({
    mutationFn: () => closeFinding(f.id),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: fail,
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <Modal open onClose={onClose} title={`${f.ref} — ${f.title}`} wide>
      <div className="space-y-5 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${TYPE_STYLE[f.type].cls}`}
          >
            {TYPE_STYLE[f.type].label}
          </span>
          <span className="text-text-muted">
            {STAGE_LABEL[view.stage]} · raised {f.raisedOn} by {f.raisedBy?.name ?? "?"}
            {f.control ? ` · ${f.control.ref}` : ""}
          </span>
        </div>

        <p className="whitespace-pre-wrap text-text-secondary">{f.description}</p>

        {view.blockers.length > 0 && (
          <ul className="space-y-1">
            {view.blockers.map((b, i) => (
              <li key={`${b.rule}-${i}`} className="text-warning">
                <code className="mr-2 rounded bg-bg-secondary px-1.5 py-0.5 text-xs">{b.rule}</code>
                {b.message}
              </li>
            ))}
          </ul>
        )}

        <div>
          <h3 className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
            Corrective actions
          </h3>
          <ul className="space-y-3">
            {view.actions.map((a) => (
              <li key={a.id} className="rounded-token-md border border-border p-3">
                <div className="font-medium text-text-heading">{a.action}</div>
                {a.rootCause && (
                  <div className="mt-1 text-xs text-text-secondary">
                    <span className="text-text-muted">Root cause:</span> {a.rootCause}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-text-muted">
                  <span>{a.owner?.name ?? "unowned"}</span>
                  <span>due {a.targetDate ?? "—"}</span>
                  {a.completedOn ? (
                    <span className="text-success">completed {a.completedOn}</span>
                  ) : (
                    <button
                      onClick={() => patch.mutate({ id: a.id, input: { completedOn: today() } })}
                      className="text-accent hover:underline"
                    >
                      Mark complete
                    </button>
                  )}
                  {a.verifiedOn ? (
                    <span className="text-success">
                      verified effective {a.verifiedOn} by {a.verifiedBy?.name ?? "?"}
                    </span>
                  ) : a.completedOn ? (
                    <button
                      onClick={() => setVerifyFor(a.id)}
                      className="text-accent hover:underline"
                    >
                      Verify effectiveness
                    </button>
                  ) : null}
                </div>
                {a.verificationNote && (
                  <div className="mt-1 text-xs italic text-text-muted">“{a.verificationNote}”</div>
                )}
                {verifyFor === a.id && (
                  <div className="mt-2 flex gap-2">
                    <input
                      value={verifyNote}
                      onChange={(e) => setVerifyNote(e.target.value)}
                      placeholder="How was it proven to work? (a re-test, a clean sample, a report)"
                      className="min-w-0 flex-1 rounded-md border border-border bg-bg-card px-3 py-2 text-sm text-text-heading placeholder:text-text-muted"
                    />
                    <button
                      onClick={() => verify.mutate(a.id)}
                      disabled={!verifyNote.trim() || verify.isPending}
                      className="rounded-token-md bg-accent text-on-accent px-3 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
                    >
                      Verify
                    </button>
                  </div>
                )}
              </li>
            ))}
            {view.actions.length === 0 && <li className="text-text-muted">None yet.</li>}
          </ul>
        </div>

        {!f.closedOn && (
          <div className="space-y-2 rounded-token-md border border-border p-3">
            <input
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="What will be done"
              className={inputCls}
            />
            <input
              value={rootCause}
              onChange={(e) => setRootCause(e.target.value)}
              placeholder="Why it happened — the root cause, not a restatement"
              className={inputCls}
            />
            <div className="flex flex-wrap gap-2">
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="rounded-md border border-border bg-bg-card px-2 py-1.5 text-sm text-text-heading"
              />
              <button
                onClick={() => add.mutate()}
                disabled={!action.trim() || add.isPending}
                className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
              >
                Add action
              </button>
            </div>
          </div>
        )}

        {!f.closedOn ? (
          <button
            onClick={() => close.mutate()}
            disabled={close.isPending}
            className="rounded-token-md border border-border bg-bg-card px-4 py-2 text-sm font-medium text-text-heading hover:bg-bg-card-hover disabled:opacity-60"
          >
            Close finding
          </button>
        ) : (
          <p className="text-xs text-text-muted">Closed {f.closedOn}.</p>
        )}

        {error && <p className="text-xs text-danger">{error}</p>}
      </div>
    </Modal>
  );
}

/* ── Programme ─────────────────────────────────────────────────────────── */

function ProgrammeTab() {
  const qc = useQueryClient();
  const { data: programmes, isLoading } = useQuery({
    queryKey: ["audit-programmes"],
    queryFn: listAuditProgrammes,
  });
  const { data: users } = useQuery({ queryKey: ["users"], queryFn: listUsers });
  const { data: controls } = useQuery({
    queryKey: ["compliance-controls"],
    queryFn: listComplianceControls,
  });
  const [error, setError] = useState<string | null>(null);
  const [newEngagement, setNewEngagement] = useState<string | null>(null);

  const year = new Date().getFullYear();
  const createProgramme = useMutation({
    mutationFn: () =>
      createAuditProgramme({
        name: `${year}–${year + 2} audit programme`,
        startsOn: `${year}-01-01`,
        endsOn: `${year + 2}-12-31`,
        objectives: "Cover every applicable control at least once across the cycle.",
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["audit-programmes"] }),
    onError: (e: Error) => setError(e.message),
  });

  const invalidate = () => void qc.invalidateQueries({ queryKey: ["audit-programmes"] });

  const assignAuditor = useMutation({
    mutationFn: ({ id, auditorUserId, external }: { id: string; auditorUserId: string | null; external: string | null }) =>
      updateAuditEngagement(id, { auditorUserId, auditorExternalName: external }),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  const scope = useMutation({
    mutationFn: ({ id, controlIds }: { id: string; controlIds: string[] }) =>
      setEngagementScope(id, controlIds),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (isLoading) {
    return (
      <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin text-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-xs text-danger">{error}</p>}

      {(!programmes || programmes.length === 0) && (
        <div className="grid place-items-center rounded-token-lg border border-border bg-bg-card py-16 text-center">
          <div className="max-w-md">
            <h2 className="font-display text-lg font-semibold text-text-heading">
              No audit programme
            </h2>
            <p className="mx-auto mt-1.5 text-sm text-text-secondary">
              Clause 9.2.2 wants a programme covering the whole ISMS over a cycle, not one
              annual audit. Start a three-year cycle and add engagements to it.
            </p>
            <button
              onClick={() => createProgramme.mutate()}
              className="mt-4 rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
            >
              Start a programme
            </button>
          </div>
        </div>
      )}

      {programmes?.map((p) => (
        <section key={p.programme.id} className="rounded-token-lg border border-border bg-bg-card">
          <header className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
            <h2 className="font-display text-sm font-semibold text-text-heading">
              {p.programme.name}
            </h2>
            <span className="text-xs text-text-muted">
              {p.programme.startsOn} → {p.programme.endsOn} · coverage{" "}
              <span className={p.blockers.length > 0 ? "text-warning" : "text-success"}>
                {p.coveredControlCount}/{p.applicableControlCount}
              </span>
            </span>
            <button
              onClick={() => setNewEngagement(p.programme.id)}
              className="ml-auto rounded-token-md border border-border bg-bg-card px-3 py-1.5 text-xs font-medium text-text-heading hover:bg-bg-card-hover"
            >
              Add engagement
            </button>
          </header>

          {p.blockers.map((b) => (
            <p key={b.rule} className="border-b border-border-light px-4 py-2 text-xs text-warning">
              {b.message}
            </p>
          ))}

          <ul>
            {p.engagements.map((e) => (
              <li key={e.engagement.id} className="border-b border-border-light px-4 py-3">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium text-text-heading">{e.engagement.title}</span>
                  <span className="text-xs text-text-muted">
                    {e.status} · {e.controls.length} control(s) in scope · {e.findingCount} finding(s)
                  </span>
                </div>
                <p className="mt-1 text-xs text-text-secondary">{e.engagement.scope}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <select
                    value={e.engagement.auditorUserId ?? ""}
                    onChange={(ev) =>
                      assignAuditor.mutate({
                        id: e.engagement.id,
                        auditorUserId: ev.target.value || null,
                        external: ev.target.value ? null : e.engagement.auditorExternalName,
                      })
                    }
                    className="rounded-md border border-border bg-bg-card px-2 py-1 text-xs text-text-heading"
                  >
                    <option value="">— internal auditor —</option>
                    {users
                      ?.filter((u) => u.active)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                  </select>
                  <input
                    defaultValue={e.engagement.auditorExternalName ?? ""}
                    onBlur={(ev) =>
                      assignAuditor.mutate({
                        id: e.engagement.id,
                        auditorUserId: ev.target.value ? null : e.engagement.auditorUserId,
                        external: ev.target.value || null,
                      })
                    }
                    placeholder="…or external auditor"
                    className="rounded-md border border-border bg-bg-card px-2 py-1 text-xs text-text-heading placeholder:text-text-muted"
                  />
                  <select
                    value=""
                    onChange={(ev) => {
                      if (!ev.target.value) return;
                      scope.mutate({
                        id: e.engagement.id,
                        controlIds: [...e.controls.map((c) => c.id), ev.target.value],
                      });
                    }}
                    className="rounded-md border border-border bg-bg-card px-2 py-1 text-xs text-text-heading"
                  >
                    <option value="">+ add control to scope</option>
                    {controls
                      ?.filter((c) => !e.controls.some((x) => x.id === c.control.id))
                      .map((c) => (
                        <option key={c.control.id} value={c.control.id}>
                          {c.control.ref} · {c.control.name}
                        </option>
                      ))}
                  </select>
                  {e.controls.length > 0 && (
                    <span className="text-text-muted">
                      {e.controls.map((c) => c.ref).join(", ")}
                    </span>
                  )}
                </div>
              </li>
            ))}
            {p.engagements.length === 0 && (
              <li className="px-4 py-3 text-sm text-text-muted">No engagements planned yet.</li>
            )}
          </ul>
        </section>
      ))}

      {newEngagement && (
        <AddEngagementModal
          programmeId={newEngagement}
          onClose={() => setNewEngagement(null)}
          onCreated={invalidate}
        />
      )}
    </div>
  );
}

function AddEngagementModal({
  programmeId,
  onClose,
  onCreated,
}: {
  programmeId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const [criteria, setCriteria] = useState("ISO 27001:2022 clauses and mapped Annex A controls");

  const create = useMutation({
    mutationFn: () =>
      createAuditEngagement({ programmeId, title: title.trim(), scope: scope.trim(), criteria }),
    onSuccess: () => {
      onCreated();
      onClose();
    },
  });

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
  return (
    <Modal
      open
      onClose={onClose}
      title="Add engagement"
      footer={
        <button
          onClick={() => create.mutate()}
          disabled={!title.trim() || !scope.trim() || create.isPending}
          className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
        >
          Add
        </button>
      }
    >
      <div className="space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Access management audit"
          className={inputCls}
          autoFocus
        />
        <textarea
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          rows={2}
          placeholder="What will be examined"
          className={inputCls}
        />
        <textarea
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          rows={2}
          placeholder="What it is examined against"
          className={inputCls}
        />
        {create.isError && <p className="text-xs text-danger">{(create.error as Error).message}</p>}
      </div>
    </Modal>
  );
}

/* ── Auditor access ────────────────────────────────────────────────────── */

function ShareTab({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [expiresOn, setExpiresOn] = useState(plusDays(30));
  const [allowDownload, setAllowDownload] = useState(false);
  const [issued, setIssued] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: shares } = useQuery({
    queryKey: ["auditor-shares"],
    queryFn: listAuditorShares,
    enabled: isAdmin,
  });

  const create = useMutation({
    mutationFn: () =>
      createAuditorShare({ label: label.trim(), expiresOn, allowEvidenceDownload: allowDownload }),
    onSuccess: (r) => {
      setIssued(`${window.location.origin}${r.url}`);
      setLabel("");
      setError(null);
      void qc.invalidateQueries({ queryKey: ["auditor-shares"] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => revokeAuditorShare(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["auditor-shares"] }),
  });

  if (!isAdmin) {
    return (
      <p className="rounded-token-lg border border-border bg-bg-card px-4 py-6 text-sm text-text-secondary">
        Issuing auditor access is admin-only — it hands out a read-only view of the whole
        compliance record.
      </p>
    );
  }

  const inputCls =
    "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";

  return (
    <div className="space-y-5">
      <div className="rounded-token-lg border border-border bg-bg-card p-4">
        <h2 className="mb-1 font-display text-sm font-semibold text-text-heading">
          Issue a read-only link
        </h2>
        <p className="mb-3 text-xs text-text-secondary">
          The auditor gets controls, policies, the operating record, the SoA, risks,
          findings and corrective actions — no portal account, no write access of any
          kind. The link is shown once and only its hash is stored, so it cannot be
          recovered later; issue a new one if it's lost.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Who it's for — e.g. BSI surveillance audit"
            className={`${inputCls} max-w-sm`}
          />
          <label className="text-xs text-text-muted">
            Expires
            <input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="flex items-center gap-2 pb-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={allowDownload}
              onChange={(e) => setAllowDownload(e.target.checked)}
            />
            Allow evidence downloads
          </label>
          <button
            onClick={() => create.mutate()}
            disabled={!label.trim() || create.isPending}
            className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60"
          >
            Issue link
          </button>
        </div>
        {error && <p className="mt-2 text-xs text-danger">{error}</p>}
        {issued && (
          <div className="mt-3 rounded-token-md border border-accent bg-accent-muted p-3">
            <p className="mb-1 text-xs font-semibold text-text-heading">
              Copy this now — it will not be shown again:
            </p>
            <code className="block break-all text-xs text-text">{issued}</code>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-token-lg border border-border bg-bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[0.65rem] uppercase tracking-wider text-text-muted">
              <th className="px-4 py-2.5 font-semibold">Label</th>
              <th className="px-4 py-2.5 font-semibold">Expires</th>
              <th className="px-4 py-2.5 font-semibold">Evidence</th>
              <th className="px-4 py-2.5 font-semibold">Accessed</th>
              <th className="px-4 py-2.5 font-semibold">State</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {shares?.map((s) => {
              const expired = s.expiresOn < today();
              const live = !s.revokedAt && !expired;
              return (
                <tr key={s.id} className="border-b border-border-light">
                  <td className="px-4 py-2.5 text-text-heading">{s.label}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {s.expiresOn}
                  </td>
                  <td className="px-4 py-2.5 text-text-secondary">
                    {s.allowEvidenceDownload ? "downloadable" : "metadata only"}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-text-secondary">
                    {s.accessCount === 0
                      ? "never"
                      : `${s.accessCount}× · ${new Date(s.lastAccessedAt!).toLocaleDateString()}`}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={
                        live ? "text-success" : s.revokedAt ? "text-danger" : "text-text-muted"
                      }
                    >
                      {live ? "live" : s.revokedAt ? "revoked" : "expired"}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {live && (
                      <button
                        onClick={() => revoke.mutate(s.id)}
                        className="text-xs text-text-muted hover:text-danger"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {(!shares || shares.length === 0) && (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-sm text-text-muted">
                  No auditor links issued.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
